// Drawing helpers for the procedural placeholders (art-assets §3–§6): hand-jittered ink lines and
// circles seeded with the same noise as the sketch cell outline (render/noise.ts), and "classic"
// balls (solid fill, darker same-hue rim) matching the default arena look.
//
// Every placeholder is our own code; nothing imitates third-party art (art-assets intro).

import {
  classicOutlineColor,
  mixRgb,
  PLAYER_PALETTE,
  type Rgb,
  SPECIAL_COLORS,
} from '@ballio/shared';
import { type Canvas2D, type Ctx2D, paintCanvas } from '../../render/canvas.js';
import { noise3, Wobble } from '../../render/noise.js';
import { css, cssAlpha } from '../../render/theme.js';

export const TAU = Math.PI * 2;
export const INK: Rgb = SPECIAL_COLORS.ink;
export const PAPER_LIGHT: Rgb = 0xfbf7ee;
export const PAPER_DARK: Rgb = 0x1e2622;
export const WHITE: Rgb = 0xffffff;

/** The font used by placeholders that contain text (bundled Fredoka, ui-flow §1). */
export const PLACEHOLDER_FONT = 'Fredoka, "Trebuchet MS", "Segoe UI", system-ui, sans-serif';

/** Palette colour `i` (wrapping). */
export function paletteColor(i: number): Rgb {
  const n = PLAYER_PALETTE.length;
  return PLAYER_PALETTE[((i % n) + n) % n] ?? SPECIAL_COLORS.plain;
}

/** A canvas of `w × h` drawn by `draw`; null when no canvas is available. */
export function art(
  w: number,
  h: number,
  draw: (ctx: Ctx2D, w: number, h: number) => void,
): Canvas2D | null {
  return paintCanvas(w, h, (ctx, cw, ch) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    draw(ctx, cw, ch);
  });
}

const wobble = new Wobble();

/** A closed, slightly wobbly circle sub-path (seeded): the hand-drawn ring. */
export function inkCirclePath(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  r: number,
  seed: number,
  amount = 0.02,
  vertices = 72,
): void {
  wobble.seed(seed, 0);
  for (let i = 0; i <= vertices; i++) {
    const a = (i / vertices) * TAU;
    const rr = r * (1 + amount * wobble.at(a));
    const x = cx + rr * Math.cos(a);
    const y = cy + rr * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** An open polyline sub-path through `pts` (x0, y0, x1, y1, …) with seeded perpendicular jitter. */
export function inkPolyPath(
  ctx: Ctx2D,
  pts: readonly number[],
  seed: number,
  jitter = 1.5,
  closed = false,
): void {
  const n = Math.floor(pts.length / 2);
  if (n < 2) return;
  let first = true;
  const segs = closed ? n : n - 1;
  for (let s = 0; s < segs; s++) {
    const x0 = pts[2 * s] ?? 0;
    const y0 = pts[2 * s + 1] ?? 0;
    const x1 = pts[2 * ((s + 1) % n)] ?? 0;
    const y1 = pts[2 * ((s + 1) % n) + 1] ?? 0;
    const len = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.max(1, Math.round(len / 12));
    const nx = len > 0 ? -(y1 - y0) / len : 0;
    const ny = len > 0 ? (x1 - x0) / len : 0;
    for (let i = 0; i <= steps; i++) {
      if (i === 0 && !first) continue;
      const t = i / steps;
      const edge = i === 0 || i === steps ? 0.3 : 1;
      const j = jitter * edge * noise3(seed, s * 64 + i, 7);
      const x = x0 + (x1 - x0) * t + nx * j;
      const y = y0 + (y1 - y0) * t + ny * j;
      if (first) {
        ctx.moveTo(x, y);
        first = false;
      } else {
        ctx.lineTo(x, y);
      }
    }
  }
  if (closed) ctx.closePath();
}

/** A straight jittered line sub-path. */
export function inkLinePath(
  ctx: Ctx2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  seed: number,
  jitter = 1.5,
): void {
  inkPolyPath(ctx, [x0, y0, x1, y1], seed, jitter);
}

/** Strokes the current path in ink (or `color`). */
export function strokeInk(ctx: Ctx2D, width: number, color: Rgb = INK, alpha = 1): void {
  ctx.lineWidth = width;
  ctx.strokeStyle = alpha >= 1 ? css(color) : cssAlpha(color, alpha);
  ctx.stroke();
}

/** Fills the current path. */
export function fillWith(ctx: Ctx2D, color: Rgb, alpha = 1): void {
  ctx.fillStyle = alpha >= 1 ? css(color) : cssAlpha(color, alpha);
  ctx.fill();
}

export type FaceKind = 'dots' | 'smile' | 'grin' | 'x' | 'sad';

/** A doodle face on a ball of radius `r` centred at (cx, cy). */
export function drawFace(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  r: number,
  kind: FaceKind,
  seed = 1,
): void {
  const ex = r * 0.3;
  const ey = cy - r * 0.12;
  const lw = Math.max(1.5, r * 0.08);
  if (kind === 'x') {
    const s = r * 0.12;
    for (const sx of [-ex, ex]) {
      ctx.beginPath();
      inkLinePath(ctx, cx + sx - s, ey - s, cx + sx + s, ey + s, seed + sx, r * 0.01);
      inkLinePath(ctx, cx + sx + s, ey - s, cx + sx - s, ey + s, seed + sx + 3, r * 0.01);
      strokeInk(ctx, lw);
    }
  } else {
    for (const sx of [-ex, ex]) {
      ctx.beginPath();
      ctx.ellipse(cx + sx, ey, r * 0.075, r * 0.11, 0, 0, TAU);
      fillWith(ctx, INK);
    }
  }
  if (kind === 'smile' || kind === 'grin' || kind === 'sad' || kind === 'x') {
    ctx.beginPath();
    const my = cy + r * (kind === 'sad' || kind === 'x' ? 0.42 : 0.18);
    const span = kind === 'grin' ? 0.42 : 0.3;
    const a0 = kind === 'sad' || kind === 'x' ? Math.PI * 1.2 : Math.PI * 0.15;
    const a1 = kind === 'sad' || kind === 'x' ? Math.PI * 1.8 : Math.PI * 0.85;
    ctx.arc(cx, my - (kind === 'sad' || kind === 'x' ? 0 : r * 0.1), r * span, a0, a1);
    strokeInk(ctx, lw);
  }
}

export interface BallOptions {
  /** Rim width as a share of r (classic look). */
  readonly rim?: number;
  /** Ink outline instead of the darker same-hue rim. */
  readonly ink?: boolean;
  readonly face?: FaceKind | null;
  readonly seed?: number;
  /** A soft highlight dot. */
  readonly highlight?: boolean;
}

/** A ball: solid fill with a darker same-hue rim (or an ink outline), optional face. */
export function drawBall(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  r: number,
  color: Rgb,
  opts: BallOptions = {},
): void {
  const rim = Math.max(1, r * (opts.rim ?? 0.08));
  const seed = opts.seed ?? 1;
  ctx.beginPath();
  if (opts.ink) inkCirclePath(ctx, cx, cy, r - rim / 2, seed, 0.015);
  else ctx.arc(cx, cy, Math.max(0, r - rim / 2), 0, TAU);
  fillWith(ctx, color);
  strokeInk(ctx, rim, opts.ink ? INK : classicOutlineColor(color));
  if (opts.highlight) {
    ctx.beginPath();
    ctx.arc(cx - r * 0.32, cy - r * 0.32, r * 0.22, 0, TAU);
    fillWith(ctx, mixRgb(color, WHITE, 0.35));
  }
  if (opts.face) drawFace(ctx, cx, cy, r, opts.face, seed);
}

/** A closed spiky sub-path (virus doodle): `spikes` points, tips `depth·r` beyond the valleys. */
export function spikyPath(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  r: number,
  spikes: number,
  depth: number,
  seed: number,
  jitter = 0,
  rotation = 0,
): void {
  const n = spikes * 2;
  for (let i = 0; i <= n; i++) {
    const k = i % n;
    const a = rotation + (k / n) * TAU;
    let rr = k % 2 === 0 ? r * (1 + depth / 2) : r * (1 - depth / 2);
    if (jitter > 0) rr *= 1 + jitter * noise3(seed, k, 3);
    const x = cx + rr * Math.cos(a);
    const y = cy + rr * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** Clips the context to the circle inscribed in a `w × h` canvas. */
export function clipToCircle(ctx: Ctx2D, w: number, h: number): void {
  ctx.beginPath();
  ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, TAU);
  ctx.closePath();
  ctx.clip();
}

/** Bold placeholder text centred at (x, y), fill plus outline. */
export function drawLabel(
  ctx: Ctx2D,
  text: string,
  x: number,
  y: number,
  px: number,
  fill: Rgb,
  outline: Rgb | null,
  outlineW = px * 0.12,
): void {
  ctx.font = `700 ${Math.round(px)}px ${PLACEHOLDER_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (outline !== null) {
    ctx.lineWidth = outlineW * 2;
    ctx.strokeStyle = css(outline);
    ctx.strokeText(text, x, y);
  }
  ctx.fillStyle = css(fill);
  ctx.fillText(text, x, y);
}
